import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";

const TeamstForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [organisationsLoaded, setOrganisationsLoaded] = useState(false);
  const [departmentsLoaded, setDepartmentsLoaded] = useState(false);
  const [teamDataLoaded, setTeamDataLoaded] = useState(false);
  const isInitialLoad = useRef(true);

  const [organisations, setOrganisations] = useState<
    { id: string; name: string }[]
  >([]);
  const [departments, setDepartments] = useState<
    { id: string; name: string }[]
  >([]);

  const formik = useFormik({
    initialValues: {
      organisationId: "",
      departmentId: "",
      team_name: "",
    },
    validationSchema: Yup.object({
      organisationId: Yup.string()
        .required("Organisation is required")
        .test("not-empty", "Organisation is required", (value) => value !== ""),
      departmentId: Yup.string()
        .required("Department is required")
        .test("not-empty", "Department is required", (value) => value !== ""),
      team_name: Yup.string()
        .required("Team Name is required")
        .trim()
        .min(1, "Team Name is required")
        .matches(
          /^[a-zA-Z0-9\s]+$/,
          "Team Name can only contain letters, numbers, and spaces"
        ),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (
      values,
      { setSubmitting, setFieldTouched, validateForm }
    ) => {
      // Mark all fields as touched to show validation errors on submit
      setFieldTouched("organisationId", true);
      setFieldTouched("departmentId", true);
      setFieldTouched("team_name", true);

      // Validate all fields
      const errors = await validateForm();
      if (Object.keys(errors).length > 0) {
        setSubmitting(false);
        return;
      }

      try {
        const payload = {
          organization_id: values.organisationId,
          department_id: values.departmentId,
          team_name: values.team_name.trim(),
          ...(id && { team_id: id }), // only add team_id for update
        };

        const response = id
          ? await Axios.put(`/teams/${id}/update/`, payload)
          : await Axios.post("/teams/create/", payload);

        if (response.status === 200 || response.status === 201) {
          toast.success(`Team ${id ? "updated" : "created"} successfully`);
          navigate("/team-list", { state: { clearState: !id } });
        }
      } catch (error: any) {
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("The teams name already exist");
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Fetch organisations dropdown
  useEffect(() => {
    if (id) {
      setLoading(true);
    }
    Axios.get("/organization/all-org/")
      .then((res) => {
        const mapped =
          res.data?.data?.map((org: any) => ({
            id: org.organization_id,
            name: org.organization_name,
          })) || [];
        setOrganisations(mapped);
        setOrganisationsLoaded(true);
      })
      .catch((error) => {
        console.error("Failed to fetch organisations:", error);
        setOrganisationsLoaded(true);
      });
  }, [id]);

  // Fetch departments dropdown
  useEffect(() => {
    const organisationId = formik.values.organisationId;
    const departmentId = formik.values.departmentId;
    const setFieldValue = formik.setFieldValue;

    if (!organisationId) {
      setDepartments([]);
      setFieldValue("departmentId", "", false);
      // If in edit mode during initial load and no organisationId, departments are considered loaded
      // Otherwise, if not in edit mode, consider departments loaded (nothing to load)
      if (id && isInitialLoad.current) {
        // Only set to true if we're waiting for initial load and there's no orgId yet
        // This means team data hasn't loaded yet, so we'll wait
        setDepartmentsLoaded(false);
      } else if (!id) {
        setDepartmentsLoaded(true);
      }
      return;
    }

    // In edit mode during initial load, mark departments as not loaded so we wait for the fetch
    if (id && isInitialLoad.current) {
      setDepartmentsLoaded(false);
    }

    Axios.get(`/department/by-org/?organization_id=${organisationId}`)
      .then((res) => {
        console.log(res);
        const mapped =
          res.data?.data?.map((dept: any) => ({
            id: dept.department_id,
            name: dept.department_name,
          })) || [];

        setDepartments(mapped);
        setDepartmentsLoaded(true);

        if (!mapped.find((d: any) => d.id === departmentId)) {
          setFieldValue("departmentId", "", false);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch departments:", error);
        setDepartments([]);
        setFieldValue("departmentId", "", false);
        setDepartmentsLoaded(true);
      });
  }, [
    formik.values.organisationId,
    formik.values.departmentId,
    formik.setFieldValue,
    id,
  ]);

  // console.log("departmentId", formik.values.departmentId);

  useEffect(() => {
    if (id) {
      setLoading(true);
      setTeamDataLoaded(false);
      Axios.get(`/teams/${id}/get/`)
        .then((res) => {
          const data = res.data?.data;
          console.log("data", data);
          formik.setValues({
            organisationId: data.organization_id || "",
            departmentId: data.department_id || "",
            team_name: data.team_name || "",
          });
          setTeamDataLoaded(true);
        })
        .catch((error) => {
          console.log(error);
          setTeamDataLoaded(true);
        });
    } else {
      setTeamDataLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Check if all data is loaded in edit mode before setting loading to false
  useEffect(() => {
    if (id && isInitialLoad.current) {
      // In edit mode, wait for all data to be loaded
      if (organisationsLoaded && departmentsLoaded && teamDataLoaded) {
        setLoading(false);
        isInitialLoad.current = false;
      }
    } else if (!id) {
      // In create mode, loading is not needed
      setLoading(false);
    }
  }, [organisationsLoaded, departmentsLoaded, teamDataLoaded, id]);

  const safeOrgOptions = useMemo(() => {
    if (
      formik.values.organisationId &&
      !organisations.some((org) => org.id === formik.values.organisationId)
    ) {
      return [{ id: formik.values.organisationId, name: "" }, ...organisations];
    }
    return organisations;
  }, [formik.values.organisationId, organisations]);

  const safeDeptOptions = useMemo(() => {
    if (
      formik.values.departmentId &&
      !departments.some((dept) => dept.id === formik.values.departmentId)
    ) {
      return [{ id: formik.values.departmentId, name: "" }, ...departments];
    }
    return departments;
  }, [formik.values.departmentId, departments]);

  const LoadingCard = () => (
    <div className="text-center py-5 bg-white rounded-3 shadow-sm">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  // console.log(safeDeptOptions, "safeDeptOptions");
  // console.log(departments, "safeOrgOptions");
  return (
    <PageContainer>
      <FormHeader
        title={`${id ? "Edit" : "Create"} Team`}
        firstLabel="Team"
        secondLabel={id ? "Edit Team" : "Add Team"}
        handleArrowClick={() => navigate("/team-list")}
      />

      {loading ? (
        <LoadingCard />
      ) : (
        <>
          <Card className="p-4 shadow-sm mt-4">
            <Form noValidate onSubmit={formik.handleSubmit}>
              <Row className="mb-4">
                {/* Organisation */}
                <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold">
                    Organisation <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={safeOrgOptions}
                    getOptionLabel={(option: any) => option.name || ""}
                    value={
                      safeOrgOptions.find(
                        (org) => org.id === formik.values.organisationId
                      ) || null
                    }
                    onChange={(_, value) => {
                      formik.setFieldValue(
                        "organisationId",
                        value?.id || "",
                        true
                      );
                      formik.setFieldTouched("organisationId", true, false);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched("organisationId", true, false);
                    }}
                    slotProps={{
                      popper: {
                        sx: {
                          "& .MuiAutocomplete-listbox": {
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: "14px",
                          },
                          "& .MuiAutocomplete-option": {
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: "14px",
                          },
                        },
                      },
                    }}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.name
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Organisation"
                        error={
                          formik.touched.organisationId &&
                          Boolean(formik.errors.organisationId)
                        }
                        helperText={
                          formik.touched.organisationId &&
                          formik.errors.organisationId
                            ? formik.errors.organisationId
                            : ""
                        }
                        fullWidth
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                  />
                </Col>

                {/* Department */}
                <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold">
                    Department <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={safeDeptOptions}
                    getOptionLabel={(option: any) => option.name || ""}
                    value={
                      safeDeptOptions.find(
                        (dept) => dept.id === formik.values.departmentId
                      ) || null
                    }
                    onChange={(_, value) => {
                      formik.setFieldValue(
                        "departmentId",
                        value?.id || "",
                        true
                      );
                      formik.setFieldTouched("departmentId", true, false);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched("departmentId", true, false);
                    }}
                    slotProps={{
                      popper: {
                        sx: {
                          "& .MuiAutocomplete-listbox": {
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: "14px",
                          },
                          "& .MuiAutocomplete-option": {
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: "14px",
                          },
                        },
                      },
                    }}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.name
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Department"
                        error={
                          formik.touched.departmentId &&
                          Boolean(formik.errors.departmentId)
                        }
                        helperText={
                          formik.touched.departmentId &&
                          formik.errors.departmentId
                            ? formik.errors.departmentId
                            : ""
                        }
                        fullWidth
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                    disabled={departments.length === 0}
                  />
                </Col>

                {/* Team Name */}
                <Col md={4} xs={12}>
                  <Form.Label className="fw-semibold">
                    Team Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="team_name"
                    placeholder="Enter Team Name"
                    value={formik.values.team_name}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.setFieldTouched("team_name", true, false);
                    }}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      formik.setFieldTouched("team_name", true, false);
                    }}
                    isInvalid={
                      formik.touched.team_name &&
                      Boolean(formik.errors.team_name)
                    }
                    style={{
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: "13px",
                    }}
                  />
                  {formik.touched.team_name && formik.errors.team_name && (
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: "12px",
                        display: "block",
                        color: "#d32f2f",
                      }}
                    >
                      {formik.errors.team_name}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Row>

              <div className="d-flex justify-content-center gap-3 mt-3">
                <CancelBackButton to="/team-list" />

                {/* Reusable submit button with spinner */}
                <SubmitButton
                  isSubmitting={formik.isSubmitting}
                  isEdit={!!id}
                />
              </div>
            </Form>
          </Card>
        </>
      )}
    </PageContainer>
  );
};

export default TeamstForm;
